import { useQuery } from '@apollo/client';
import { Box, MenuItem, TextField } from '@mui/material';
import Page from 'components/Page';
import { SortEnumType } from 'lib/gql/queryTypes';
import { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import CrashReportGrid from './CrashReportGrid';
import { crashReportOwnersQuery, legacyServerVersionsQuery } from './queries';
import {
  CrashReportLegacyServerVersionsQuery,
  CrashReportLegacyServerVersionsQueryVariables,
  CrashReportOwnersQuery,
  CrashReportOwnersQueryVariables,
} from './types/queries';

const CrashReportsPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerId = searchParams.get('ownerId') ?? undefined;
  const legacyServerVersionId =
    searchParams.get('legacyServerVersionId') ?? undefined;
  const { data, loading } = useQuery<
    CrashReportLegacyServerVersionsQuery,
    CrashReportLegacyServerVersionsQueryVariables
  >(legacyServerVersionsQuery, {
    variables: {
      order: [
        { major: SortEnumType.Desc },
        { minor: SortEnumType.Desc },
        { build: SortEnumType.Desc },
        { revision: SortEnumType.Desc },
      ],
    },
  });
  const { data: ownersData, loading: ownersLoading } = useQuery<
    CrashReportOwnersQuery,
    CrashReportOwnersQueryVariables
  >(crashReportOwnersQuery, {
    variables: { order: [{ name: SortEnumType.Asc }] },
  });

  const setOwner = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('ownerId', value);
    else next.delete('ownerId');
    setSearchParams(next);
  };

  const setLegacyServerVersion = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('legacyServerVersionId', value);
    else next.delete('legacyServerVersionId');
    setSearchParams(next);
  };

  return (
    <Page title="Crash Reports">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Legacy server version"
            value={legacyServerVersionId ?? ''}
            disabled={loading}
            onChange={(event) => setLegacyServerVersion(event.target.value)}
            sx={{ minWidth: 230 }}
          >
            <MenuItem value="">All versions</MenuItem>
            {data?.legacyServerVersions.map((version) => (
              <MenuItem key={version.id} value={version.id}>
                {version.version}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Owner"
            value={ownerId ?? ''}
            disabled={ownersLoading}
            onChange={(event) => setOwner(event.target.value)}
            sx={{ minWidth: 230 }}
          >
            <MenuItem value="">All owners</MenuItem>
            {ownersData?.owners.map((owner) => (
              <MenuItem key={owner.id} value={owner.id}>
                {owner.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <CrashReportGrid
            ownerId={ownerId}
            legacyServerVersionId={legacyServerVersionId}
          />
        </Box>
      </Box>
    </Page>
  );
};

export default CrashReportsPage;
